import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { familiesApi, FamilyMember, FamilyMemberCreate } from '../api/families';
import {
  documentsApi,
  MedicalDocument,
  MemberDocumentsQuery,
  MemberDocumentFilterOptions,
  TimelineEventSeverity,
} from '../api/documents';
import { decryptE2eeDocument } from '../utils/e2eeClient';
import DocumentUpload from '../components/DocumentUpload';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import MemberFormModal from '../components/MemberFormModal';
import MedicalTimeline from '../components/MedicalTimeline';
import MemberBar from '../components/member/MemberBar';
import MemberHero from '../components/member/MemberHero';
import MemberTabs, { MemberTabId } from '../components/member/MemberTabs';
import MemberOverviewTab from '../components/member/MemberOverviewTab';
import MemberDocumentCard, { ReprocessAction } from '../components/member/MemberDocumentCard';
import { memberDisplayName } from '../utils/memberDisplay';
import ShareInviteModal from '../components/ShareInviteModal';
import { useAuth } from '../contexts/AuthContext';
import { useClassifiersConfig } from '../contexts/ClassifiersConfigContext';
import { useIsAdvanced } from '../utils/viewMode';
import { useIsMobileViewport } from '../utils/useIsMobileViewport';
import { useViewedAsSharedMember } from '../utils/useSharedMemberAccess';
import { subscribeSseJson } from '../utils/sse';
import './DocumentView.css';
import './AdminConfig.css';
import './MemberDetail.css';

// SSE status payload (one-of: pipeline_status, ocr/summary/classification statuses).
type DocumentStatusUpdate = {
  document_id: number;
  status?: string;
  pipeline_status?: string;
  ocr_processing_status?: string;
  summary_status?: string;
  classification_status?: string;
  message?: string;
  timestamp?: string;
};

type DocListFilter = 'all' | 'processing' | 'completed' | 'error' | 'pdf' | 'image';

const DOC_FILTER_CHIPS: { id: DocListFilter; label: string; advancedOnly?: boolean }[] = [
  { id: 'all', label: 'Toate' },
  { id: 'processing', label: 'În procesare' },
  { id: 'completed', label: 'Finalizate' },
  { id: 'error', label: 'Cu erori' },
  { id: 'pdf', label: 'PDF', advancedOnly: true },
  { id: 'image', label: 'Imagini', advancedOnly: true },
];

interface AdvFilters {
  document_types: string[];
  facets: Record<string, string>;
}

const ADV_FILTER_EMPTY: AdvFilters = { document_types: [], facets: {} };

const TAB_HASHES: Record<MemberTabId, string> = {
  overview: '#overview',
  timeline: '#timeline',
  docs: '#docs',
  observations: '#observations',
};

function tabFromHash(hash: string): MemberTabId {
  const h = hash.replace(/^#/, '').split('?')[0];
  if (h === 'timeline') return 'timeline';
  if (h === 'docs' || h === 'documents') return 'docs';
  if (h === 'observations' || h === 'insights') return 'observations';
  return 'overview';
}

function severityFilterFromHash(hash: string): TimelineEventSeverity[] | null {
  const q = hash.split('?')[1];
  if (!q) return null;
  const params = new URLSearchParams(q);
  const sev = params.get('severity');
  if (!sev) return null;
  const out: TimelineEventSeverity[] = [];
  for (const part of sev.split(',')) {
    const t = part.trim() as TimelineEventSeverity;
    if (t === 'info' || t === 'notable' || t === 'important' || t === 'critical') {
      out.push(t);
    }
  }
  return out.length > 0 ? out : null;
}

function severityBannerLabel(sevs: TimelineEventSeverity[]): string {
  const has = (s: TimelineEventSeverity) => sevs.includes(s);
  if (has('important') && has('critical')) return 'Filtrat: constatări importante & critice';
  if (has('critical')) return 'Filtrat: doar evenimente critice';
  if (has('important')) return 'Filtrat: doar evenimente importante';
  return 'Filtru de severitate activ';
}

const MemberDetail: React.FC = () => {
  const isAdvanced = useIsAdvanced();
  const isMobile = useIsMobileViewport();
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { classifiers } = useClassifiersConfig();
  // Resolve "was this member shared with me directly (vs via the whole
  // family, vs being mine)?" at the top of the component so the hook
  // call stays unconditional. Falls back to `false` while still
  // resolving so family links don't flicker.
  const viewedAsSharedMember =
    useViewedAsSharedMember(memberId ? parseInt(memberId, 10) : null) ?? false;
  const [member, setMember] = useState<FamilyMember | null>(null);
  const [documents, setDocuments] = useState<MedicalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [deletingMember, setDeletingMember] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState('');

  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState('');

  const [docActiveActions, setDocActiveActions] = useState<Record<number, ReprocessAction>>({});
  const setDocAction = useCallback((docId: number, action: ReprocessAction) => {
    setDocActiveActions(prev => {
      if (action === null) {
        const { [docId]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [docId]: action };
    });
  }, []);
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [docFilter, setDocFilter] = useState<DocListFilter>('all');
  const [advFilters, setAdvFilters] = useState<AdvFilters>(() => ({
    document_types: [],
    facets: {},
  }));
  const [filterOptions, setFilterOptions] = useState<MemberDocumentFilterOptions | null>(null);
  const [docsLoading, setDocsLoading] = useState(false);

  // Active tab + URL hash sync. Initial value derived from window.location.hash
  // so deep links like .../members/5#timeline?severity=critical land directly
  // on the right panel. We persist via navigate(replace:true) so back/forward
  // navigation cycles through tabs without re-mounting the page.
  const [activeTab, setActiveTab] = useState<MemberTabId>(() => tabFromHash(location.hash));
  const [timelineSeverityFilter, setTimelineSeverityFilter] = useState<TimelineEventSeverity[] | null>(
    () => severityFilterFromHash(location.hash),
  );

  const sseAbortRef = useRef<AbortController | null>(null);

  // Sync state ← URL hash whenever the user navigates browser-history-style.
  useEffect(() => {
    const t = tabFromHash(location.hash);
    setActiveTab((prev) => (prev === t ? prev : t));
    setTimelineSeverityFilter(severityFilterFromHash(location.hash));
  }, [location.hash]);

  const switchTab = useCallback(
    (id: MemberTabId, opts?: { severity?: 'important' | 'critical' | 'all-marked' }) => {
      let hash = TAB_HASHES[id];
      if (id === 'timeline' && opts?.severity) {
        if (opts.severity === 'all-marked') {
          hash += '?severity=important,critical';
        } else {
          hash += `?severity=${opts.severity}`;
        }
      }
      navigate(hash, { replace: true });
      setActiveTab(id);
      setTimelineSeverityFilter(severityFilterFromHash(hash));
      // Move focus to the freshly-mounted panel for accessibility (a11y spec
      // calls this out explicitly). `preventScroll: true` is critical here:
      // without it, browsers auto-scroll the page so the panel sits at the
      // top of the viewport, which yanks the user past the sticky member-
      // bar + tabs-bar every time they switch tabs.
      window.requestAnimationFrame(() => {
        const panel = document.getElementById(`member-tabpanel-${id}`);
        panel?.focus({ preventScroll: true });
      });
    },
    [navigate],
  );

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(docSearchQuery), 400);
    return () => window.clearTimeout(t);
  }, [docSearchQuery]);

  const queryParams = useMemo((): MemberDocumentsQuery => {
    const q: MemberDocumentsQuery = {};
    const t = debouncedSearch.trim();
    if (t) q.q = t;
    switch (docFilter) {
      case 'processing': q.pipeline = 'processing'; break;
      case 'completed':  q.pipeline = 'completed';  break;
      case 'error':      q.pipeline = 'error';      break;
      case 'pdf':        q.pipeline = 'all'; q.file_type = 'pdf';   break;
      case 'image':      q.pipeline = 'all'; q.file_type = 'image'; break;
      default:           q.pipeline = 'all'; break;
    }
    if (advFilters.document_types.length > 0) {
      q.document_types = advFilters.document_types;
    }
    const nonEmptyFacets: Record<string, string> = {};
    for (const [k, v] of Object.entries(advFilters.facets)) {
      if (v && String(v).trim() !== '') nonEmptyFacets[k] = v;
    }
    if (Object.keys(nonEmptyFacets).length > 0) q.facets = nonEmptyFacets;
    return q;
  }, [debouncedSearch, docFilter, advFilters]);

  const hasActiveFilters = useMemo(() => {
    if (debouncedSearch.trim()) return true;
    if (docFilter !== 'all') return true;
    if (advFilters.document_types.length > 0) return true;
    return Object.values(advFilters.facets).some((v) => String(v).trim() !== '');
  }, [debouncedSearch, docFilter, advFilters]);

  const hasAdvancedFilterOptions = useMemo(() => {
    if (!filterOptions) return false;
    const f = filterOptions;
    if (f.document_types.length > 0) return true;
    return Object.values(f.facets || {}).some(
      (vals) => Array.isArray(vals) && vals.length > 0,
    );
  }, [filterOptions]);

  const memberEditInitial = useMemo((): FamilyMemberCreate => {
    if (!member) {
      return { name: '', date_of_birth: '', gender: undefined, member_relationship: '', notes: '' };
    }
    return {
      name: member.name,
      date_of_birth: member.date_of_birth ? member.date_of_birth.slice(0, 10) : '',
      gender: member.gender ?? undefined,
      member_relationship: member.member_relationship || '',
      notes: member.notes || '',
    };
  }, [member]);

  const loadMemberAndOptions = useCallback(async () => {
    if (!memberId) return;
    const mid = parseInt(memberId, 10);
    setLoading(true);
    setError('');
    try {
      const [m, opts] = await Promise.all([
        familiesApi.getMemberById(mid),
        documentsApi.getMemberDocumentFilterOptions(mid),
      ]);
      setMember(m);
      setFilterOptions(opts);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    loadMemberAndOptions();
  }, [loadMemberAndOptions]);

  useEffect(() => {
    if (!memberId) return;
    const mid = parseInt(memberId, 10);
    let cancelled = false;
    setDocsLoading(true);
    documentsApi
      .getMemberDocuments(mid, queryParams)
      .then((d) => {
        if (!cancelled) setDocuments(d);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Eroare la încărcarea documentelor');
      })
      .finally(() => {
        if (!cancelled) setDocsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, queryParams]);

  const refreshLists = useCallback(async () => {
    if (!memberId) return;
    const mid = parseInt(memberId, 10);
    try {
      const [opts, d] = await Promise.all([
        documentsApi.getMemberDocumentFilterOptions(mid),
        documentsApi.getMemberDocuments(mid, queryParams),
      ]);
      setFilterOptions(opts);
      setDocuments(d);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la reîncărcare');
    }
  }, [memberId, queryParams]);

  // SSE may fire several completion events in quick succession (OCR, then
  // summary, then classification). Collapse them into a single re-fetch
  // ~600ms after the last one to avoid hammering the list endpoint.
  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleListRefresh = useCallback(() => {
    if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
    refreshDebounceRef.current = setTimeout(() => {
      refreshDebounceRef.current = null;
      refreshLists();
    }, 600);
  }, [refreshLists]);

  // Real-time streaming via per-member SSE.
  useEffect(() => {
    if (!memberId) return;

    if (sseAbortRef.current) {
      sseAbortRef.current.abort();
      sseAbortRef.current = null;
    }

    const ac = new AbortController();
    sseAbortRef.current = ac;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    const url = `${apiUrl}/api/documents/member/${memberId}/status/stream`;
    subscribeSseJson(url, {
      signal: ac.signal,
      onMessage: (parsed: DocumentStatusUpdate) => {
        try {
          setDocuments((docs) =>
            docs.map((d) => {
              if (d.id !== parsed.document_id) return d;
              const pipe =
                parsed.pipeline_status ??
                parsed.ocr_processing_status ??
                parsed.status ??
                d.pipeline_status;
              return {
                ...d,
                ...parsed,
                pipeline_status: pipe ?? d.pipeline_status,
                ocr_processing_status:
                  parsed.ocr_processing_status ?? parsed.status ?? d.ocr_processing_status,
                summary_status: parsed.summary_status ?? d.summary_status,
                classification_status: parsed.classification_status ?? d.classification_status,
              };
            })
          );

          // Per-document spinner clear — now that classify/regenerate-summary
          // return 202 immediately, the spinner has to wait for an SSE
          // status event before clearing. We key off the action currently
          // attached to this document and only clear once a terminal stage
          // event for the matching stage arrives. Other actions (rerun-ocr,
          // rerun-all) keep their existing finally-clears the spinner
          // behaviour because those endpoints still block.
          const sseSummaryTerminal =
            parsed.summary_status === 'completed' ||
            parsed.summary_status === 'error' ||
            parsed.pipeline_status === 'summary_completed' ||
            parsed.pipeline_status === 'summary_error' ||
            parsed.status === 'summary_completed' ||
            parsed.status === 'summary_error';
          const sseClassificationTerminal =
            parsed.classification_status === 'completed' ||
            parsed.classification_status === 'error' ||
            parsed.pipeline_status === 'classification_completed' ||
            parsed.pipeline_status === 'classification_error' ||
            parsed.status === 'classification_completed' ||
            parsed.status === 'classification_error';
          if (sseSummaryTerminal || sseClassificationTerminal) {
            setDocActiveActions((prev) => {
              const cur = prev[parsed.document_id];
              if (!cur) return prev;
              if (cur === 'regenerate-summary' && sseSummaryTerminal) {
                const { [parsed.document_id]: _omit, ...rest } = prev;
                return rest;
              }
              if (cur === 'reclassify' && sseClassificationTerminal) {
                const { [parsed.document_id]: _omit, ...rest } = prev;
                return rest;
              }
              return prev;
            });
          }

          const isCompletionEvent =
            parsed.status === 'completed' ||
            parsed.status === 'classification_completed' ||
            parsed.status === 'summary_completed' ||
            parsed.pipeline_status === 'completed' ||
            parsed.pipeline_status === 'classification_completed' ||
            parsed.pipeline_status === 'summary_completed' ||
            parsed.classification_status === 'completed' ||
            parsed.summary_status === 'completed' ||
            parsed.ocr_processing_status === 'completed';
          if (isCompletionEvent) {
            scheduleListRefresh();
          }
        } catch {
          // ignore parse errors
        }
      },
    }).catch(() => {});

    return () => {
      if (sseAbortRef.current) {
        sseAbortRef.current.abort();
        sseAbortRef.current = null;
      }
      if (refreshDebounceRef.current) {
        clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = null;
      }
    };
  }, [memberId, scheduleListRefresh]);

  // ── Handlers ───────────────────────────────────────────────────

  const handleDelete = async (docId: number) => {
    if (!window.confirm('Sigur doriti sa stergeti acest document?')) return;
    try {
      await documentsApi.deleteDocument(docId);
      refreshLists();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la stergerea documentului');
    }
  };

  // The bar's "Șterge membru" kebab item now opens a ConfirmDialog
  // (replacing the old window.confirm). The actual API call lives in
  // `confirmDeleteMember`; the dialog awaits it so the spinner stays
  // on the confirm button until the request resolves.
  const handleDeleteMember = () => {
    if (!member) return;
    setError('');
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMember = async () => {
    if (!member) return;
    setDeletingMember(true);
    setError('');
    try {
      await familiesApi.deleteMember(member.id);
      setShowDeleteConfirm(false);
      navigate(`/families/${member.family_id}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la stergerea membrului');
      setShowDeleteConfirm(false);
    } finally {
      setDeletingMember(false);
    }
  };

  // Optimistically patch a single document in-place so per-stage flags flip
  // synchronously on click (spinner appears immediately on the right icon).
  // Subsequent SSE pushes will overwrite these fields with the real values.
  const patchDoc = useCallback((docId: number, patch: Partial<MedicalDocument>) => {
    setDocuments(docs => docs.map(d => (d.id === docId ? { ...d, ...patch } : d)));
  }, []);

  const handleRerunOcrOnly = async (doc: MedicalDocument) => {
    setError('');
    setDocAction(doc.id, 'rerun-ocr');
    patchDoc(doc.id, { pipeline_status: 'queued', ocr_processing_status: 'queued' });
    try {
      // Always-on E2EE: every doc is encrypted, server can't run OCR on
      // ciphertext, so we decrypt locally and POST the cleartext bytes
      // (server forwards to OCR microservice ephemerally, never
      // persists them).
      const dec = await decryptE2eeDocument(doc.id);
      await documentsApi.rerunOcrWithOriginalFile(doc.id, {
        bytes: dec.bytes,
        filename: dec.filename,
        mime: dec.mime,
        ocrOnly: true,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la repornirea OCR (doar text)');
      patchDoc(doc.id, { pipeline_status: 'error', ocr_processing_status: 'error' });
    } finally {
      setDocAction(doc.id, null);
    }
  };

  const handleRerunFullPipeline = async (doc: MedicalDocument) => {
    setError('');
    setDocAction(doc.id, 'rerun-all');
    patchDoc(doc.id, {
      pipeline_status: 'queued',
      ocr_processing_status: 'queued',
      summary_status: 'queued',
      classification_status: 'queued',
    });
    try {
      // See `handleRerunOcrOnly` — always-on E2EE forces the decrypt path.
      const dec = await decryptE2eeDocument(doc.id);
      await documentsApi.rerunOcrWithOriginalFile(doc.id, {
        bytes: dec.bytes,
        filename: dec.filename,
        mime: dec.mime,
        ocrOnly: false,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la reprocesarea completă');
      patchDoc(doc.id, {
        pipeline_status: 'error',
        ocr_processing_status: 'error',
        summary_status: 'error',
        classification_status: 'error',
      });
    } finally {
      setDocAction(doc.id, null);
    }
  };

  const handleRerunClassification = async (docId: number) => {
    setError('');
    setDocAction(docId, 'reclassify');
    patchDoc(docId, { classification_status: 'generating', pipeline_status: 'classification_generating' });
    try {
      // The endpoint now returns 202 immediately; the LLM call runs on
      // the Celery worker. The spinner is NOT cleared here — it stays
      // up until the SSE handler observes a terminal classification
      // status for this doc (see the `sseClassificationTerminal` branch
      // in the SSE subscription effect above).
      await documentsApi.classifyDocument(docId);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la reclasificare');
      patchDoc(docId, { classification_status: 'error', pipeline_status: 'classification_error' });
      // Dispatch itself failed (401/403/400/network) — no SSE event
      // will ever fire for this attempt, so clear the spinner here.
      setDocAction(docId, null);
    }
  };

  const handleRegenerateSummary = async (docId: number) => {
    setError('');
    setDocAction(docId, 'regenerate-summary');
    patchDoc(docId, { summary_status: 'generating', pipeline_status: 'summary_generating' });
    try {
      // 202-immediate; spinner waits on SSE (`sseSummaryTerminal`
      // branch in the SSE subscription effect above).
      await documentsApi.regenerateSummary(docId, 'ro');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la regenerarea rezumatului');
      patchDoc(docId, { summary_status: 'error', pipeline_status: 'summary_error' });
      setDocAction(docId, null);
    }
  };

  const handleGenerateTimeline = async () => {
    if (!memberId) return;
    setTimelineLoading(true);
    setTimelineError('');
    setMember((prev: any) => prev ? { ...prev, timeline: null } : prev);
    try {
      const result = await documentsApi.getMemberTimeline(parseInt(memberId));
      setMember((prev: any) => prev ? { ...prev, timeline: result.timeline } : prev);
    } catch (err: any) {
      setTimelineError(err.response?.data?.detail || 'Eroare la generarea cronologiei');
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleGenerateInsights = async () => {
    if (!memberId) return;
    setInsightsLoading(true);
    setInsightsError('');
    setMember((prev: any) => prev ? { ...prev, insights: null } : prev);
    try {
      const result = await documentsApi.getMemberInsights(parseInt(memberId));
      setMember((prev: any) => prev ? { ...prev, insights: result.insights } : prev);
    } catch (err: any) {
      setInsightsError(err.response?.data?.detail || 'Eroare la generarea analizei');
    } finally {
      setInsightsLoading(false);
    }
  };

  // ── Early returns ──────────────────────────────────────────────

  if (loading) {
    return (
      <div className="page-container">
        <div className="skeleton skeleton-line" style={{ width: 240, height: 28 }} />
        <div className="skeleton skeleton-card" style={{ marginTop: 24 }} />
        <div className="grid grid-2" style={{ marginTop: 24 }}>
          {[1, 2].map(i => <div key={i} className="skeleton skeleton-card" />)}
        </div>
      </div>
    );
  }

  if (!member) {
    return <div className="page-container"><div className="alert alert-error">Membru negasit</div></div>;
  }

  const canDeleteMember = Boolean(user && member.family_owner_id === user.id);
  const canManageShare = Boolean(user && member.family_owner_id === user.id);

  // ── Render ─────────────────────────────────────────────────────

  return (
    <div className="member-page member-detail-page">
      {/* Full-width sticky bar — sibling of `.page-container` so it
          spans the entire viewport like the navbar above it.
          `MemberBar` composes the reusable `<PageBar>` whose inner
          `.page-bar-inner` centres the breadcrumb + actions at the
          same 1180-max as the page content. */}
      <MemberBar
        member={member}
        canManageShare={canManageShare}
        canDeleteMember={canDeleteMember}
        deletingMember={deletingMember}
        viewedAsSharedMember={viewedAsSharedMember}
        onUpload={() => setShowUpload(true)}
        onEdit={() => setShowEditMember(true)}
        onShare={() => setShowShareModal(true)}
        onDelete={handleDeleteMember}
      />

      <div className="page-container member-page-content">
        <MemberHero
          member={member}
          documents={documents}
          filterOptions={filterOptions}
          isAdvanced={isAdvanced}
          viewedAsSharedMember={viewedAsSharedMember}
          onUpload={() => setShowUpload(true)}
          onSwitchTab={switchTab}
        />

        {error && <div className="alert alert-error" style={{ marginTop: 'var(--sp-3)' }}>{error}</div>}
      </div>

      {/* Tabs strip is also full-width (sibling of `.page-container`)
          for visual continuity with the bar above it. Tabs themselves
          are centred via `.member-tabs-bar-inner` inside `MemberTabs`. */}
      <MemberTabs
        active={activeTab}
        onChange={(id) => switchTab(id)}
        timelineCount={member.timeline?.total_events ?? null}
        docCount={filterOptions?.document_count ?? null}
        isAdvanced={isAdvanced}
      />

      <div className="page-container member-page-content">
      {activeTab === 'overview' && (
        <section
          className="member-tab-panel"
          role="tabpanel"
          id="member-tabpanel-overview"
          aria-labelledby="member-tab-overview"
          tabIndex={0}
        >
          <MemberOverviewTab
            timeline={member.timeline ?? null}
            documents={documents}
            timelineLoading={timelineLoading}
            onSwitchTab={switchTab}
            onUpload={() => setShowUpload(true)}
            onGenerateTimeline={handleGenerateTimeline}
          />
        </section>
      )}

      {activeTab === 'timeline' && (
        <section
          className="member-tab-panel"
          role="tabpanel"
          id="member-tabpanel-timeline"
          aria-labelledby="member-tab-timeline"
          tabIndex={0}
        >
          <div className="member-tab-toolbar">
            <h2 className="member-tab-toolbar-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Cronologie medicală
            </h2>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleGenerateTimeline}
              disabled={timelineLoading}
            >
              {timelineLoading ? (
                <>
                  <span className="spinner" style={{ width: 14, height: 14 }} />
                  Generare…
                </>
              ) : member.timeline ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  Regenerează
                </>
              ) : (
                'Generează'
              )}
            </button>
          </div>

          {timelineSeverityFilter && timelineSeverityFilter.length > 0 && (
            <div className="member-severity-banner" role="status">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              {severityBannerLabel(timelineSeverityFilter)}
              <button
                type="button"
                onClick={() => {
                  setTimelineSeverityFilter(null);
                  navigate(TAB_HASHES.timeline, { replace: true });
                }}
              >
                Resetează
              </button>
            </div>
          )}

          {timelineError && (
            <div className="alert alert-error" style={{ marginBottom: 'var(--sp-3)' }}>{timelineError}</div>
          )}

          {timelineLoading && !member.timeline && (
            <div className="card" style={{ padding: 'var(--sp-6)', textAlign: 'center', color: 'var(--color-slate-500)' }}>
              <div className="spinner" style={{ width: 24, height: 24, margin: '0 auto var(--sp-3)' }} />
              <div>Se analizează {filterOptions?.document_count ?? documents.length} documente…</div>
              <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--sp-1)' }}>
                Această operație poate dura câteva minute
              </div>
            </div>
          )}

          {member.timeline && (
            <MedicalTimeline
              data={member.timeline}
              initialSeverityFilter={timelineSeverityFilter ?? undefined}
            />
          )}

          {!member.timeline && !timelineLoading && !timelineError && (
            <div className="card">
              <div className="empty-state">
                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <div className="empty-state-title">Cronologia nu a fost încă generată</div>
                <div className="empty-state-text">
                  Generează o cronologie medicală din toate documentele acestui membru.
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleGenerateTimeline}
                  disabled={timelineLoading || (filterOptions?.document_count ?? 0) === 0}
                >
                  Generează cronologie
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {activeTab === 'docs' && (
        <section
          className="member-tab-panel"
          role="tabpanel"
          id="member-tabpanel-docs"
          aria-labelledby="member-tab-docs"
          tabIndex={0}
        >
          {filterOptions === null ? (
            <div className="card" style={{ borderRadius: 'var(--radius-xl)', padding: 'var(--sp-8)' }}>
              <div className="spinner" style={{ width: 28, height: 28, margin: '0 auto' }} />
            </div>
          ) : filterOptions.document_count === 0 ? (
            <div className="card" style={{ borderRadius: 'var(--radius-xl)' }}>
              <div className="empty-state">
                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                <div className="empty-state-title">Niciun document</div>
                <div className="empty-state-text">Încărcați primul document medical pentru acest membru.</div>
                <button type="button" className="btn btn-primary" onClick={() => setShowUpload(true)}>
                  Încarcă document
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="admin-stats">
                <span className="admin-stats__badge">
                  {docsLoading ? (
                    <>Se încarcă…</>
                  ) : hasActiveFilters ? (
                    <>
                      <strong>{documents.length}</strong> documente găsite
                      {filterOptions ? (
                        <>
                          {' '}
                          din <strong>{filterOptions.document_count}</strong> totale
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <strong>{documents.length}</strong> documente
                    </>
                  )}
                </span>
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setDocSearchQuery('');
                      setDocFilter('all');
                      setAdvFilters({ ...ADV_FILTER_EMPTY });
                    }}
                  >
                    Resetează filtrele
                  </button>
                )}
              </div>

              <div className="admin-toolbar-panel">
                <div className="admin-toolbar-panel__row">
                  <div className="admin-toolbar-search-block">
                    <div className="admin-search-wrap">
                      <svg
                        className="admin-search-icon"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                      </svg>
                      <input
                        id="member-doc-search"
                        type="search"
                        className="admin-search"
                        placeholder={isAdvanced ? 'Nume fișier, note, tip, text OCR…' : 'Caută în documente'}
                        value={docSearchQuery}
                        onChange={(e) => setDocSearchQuery(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="admin-toolbar-filters-block">
                    <div className="admin-filters" role="group" aria-label="Filtru documente">
                      {DOC_FILTER_CHIPS.filter((c) => isAdvanced || !c.advancedOnly).map((chip) => (
                        <button
                          key={chip.id}
                          type="button"
                          className={`admin-filter-chip${docFilter === chip.id ? ' active' : ''}`}
                          onClick={() => setDocFilter(chip.id)}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {filterOptions && hasAdvancedFilterOptions && (
                <details
                  className="admin-toolbar-panel admin-toolbar-collapsible"
                  style={{ marginTop: 'var(--sp-3)' }}
                  open={!isMobile}
                >
                  <summary className="admin-toolbar-summary">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <line x1="4" y1="6" x2="20" y2="6" />
                      <line x1="7" y1="12" x2="17" y2="12" />
                      <line x1="10" y1="18" x2="14" y2="18" />
                    </svg>
                    <span>
                      {isAdvanced ? 'Filtre avansate' : 'Filtre detaliate'}
                    </span>
                    <svg className="admin-toolbar-summary__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </summary>
                  <div
                    className="admin-toolbar-panel__row"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: 'var(--sp-4)',
                    }}
                  >
                    {filterOptions.document_types.length > 0 && (
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                        <span className="admin-toolbar-filters-label">Tip document</span>
                        <select
                          className="admin-select"
                          value={advFilters.document_types[0] ?? ''}
                          onChange={(e) =>
                            setAdvFilters((a) => ({
                              ...a,
                              document_types: e.target.value ? [e.target.value] : [],
                            }))
                          }
                        >
                          <option value="">—</option>
                          {filterOptions.document_types.map((x) => (
                            <option key={x} value={x}>
                              {x}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {classifiers
                      .filter((c) => c.show_in_filters)
                      .map((c) => {
                        const values = filterOptions.facets?.[c.key] ?? [];
                        if (values.length === 0) return null;
                        const current = advFilters.facets[c.key] || '';
                        return (
                          <label
                            key={`facet-${c.key}`}
                            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}
                          >
                            <span className="admin-toolbar-filters-label">{c.label_ro}</span>
                            <select
                              className="admin-select"
                              value={current}
                              onChange={(e) =>
                                setAdvFilters((a) => ({
                                  ...a,
                                  facets: { ...a.facets, [c.key]: e.target.value },
                                }))
                              }
                            >
                              <option value="">—</option>
                              {values.map((x) => (
                                <option key={x} value={x}>
                                  {x}
                                </option>
                              ))}
                            </select>
                          </label>
                        );
                      })}
                  </div>
                </details>
              )}

              {documents.length === 0 && !docsLoading ? (
                <div className="admin-toolbar-panel" style={{ textAlign: 'center', padding: 'var(--sp-8)' }}>
                  <p style={{ margin: 0, color: 'var(--color-slate-600)', fontSize: 'var(--text-sm)' }}>
                    Niciun document nu corespunde căutării sau filtrelor.
                  </p>
                </div>
              ) : (
                <div className="admin-grid">
                  {documents.map((doc) => (
                    <MemberDocumentCard
                      key={doc.id}
                      doc={doc}
                      isAdvanced={isAdvanced}
                      classifiers={classifiers}
                      docActiveActions={docActiveActions}
                      onRerunFullPipeline={handleRerunFullPipeline}
                      onRerunOcrOnly={handleRerunOcrOnly}
                      onRegenerateSummary={handleRegenerateSummary}
                      onRerunClassification={handleRerunClassification}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeTab === 'observations' && (
        <section
          className="member-tab-panel"
          role="tabpanel"
          id="member-tabpanel-observations"
          aria-labelledby="member-tab-observations"
          tabIndex={0}
        >
          <div className="member-tab-toolbar">
            <h2 className="member-tab-toolbar-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
              {isAdvanced ? 'Analiză & Insight-uri' : 'Observații'}
            </h2>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleGenerateInsights}
              disabled={insightsLoading || (filterOptions?.document_count ?? 0) === 0}
            >
              {insightsLoading ? (
                <>
                  <span className="spinner" style={{ width: 14, height: 14 }} />
                  Generare…
                </>
              ) : member.insights ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  Regenerează
                </>
              ) : (
                'Generează'
              )}
            </button>
          </div>

          {insightsError && (
            <div className="alert alert-error" style={{ marginBottom: 'var(--sp-3)' }}>{insightsError}</div>
          )}

          {insightsLoading && !member.insights && (
            <div className="card" style={{ padding: 'var(--sp-6)', textAlign: 'center', color: 'var(--color-slate-500)' }}>
              <div className="spinner" style={{ width: 24, height: 24, margin: '0 auto var(--sp-3)' }} />
              <div>Se analizează {filterOptions?.document_count ?? documents.length} documente…</div>
              <div style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--sp-1)' }}>
                Această operație poate dura câteva minute
              </div>
            </div>
          )}

          {member.insights && (
            <div className="card">
              <ReactMarkdown
                className="md-content"
                remarkPlugins={[remarkGfm]}
                urlTransform={(url, key) => {
                  if (key === 'src' && typeof url === 'string' && url.startsWith('data:image/')) return url;
                  return defaultUrlTransform(url);
                }}
              >
                {member.insights}
              </ReactMarkdown>
            </div>
          )}

          {!member.insights && !insightsLoading && !insightsError && (
            <div className="card">
              <div className="empty-state">
                <svg className="empty-state-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
                <div className="empty-state-title">Nicio analiză încă</div>
                <div className="empty-state-text">
                  Generează o analiză cu pattern-uri și corelații din documentele medicale.
                </div>
              </div>
            </div>
          )}
        </section>
      )}
      </div>

      {/* ── Modals ──────────────────────────────────────────────── */}

      <Modal open={showUpload} onClose={() => setShowUpload(false)} title="Încarcă document medical">
        <DocumentUpload
          memberId={member.id}
          onSuccess={() => {
            setShowUpload(false);
            refreshLists();
          }}
          onCancel={() => setShowUpload(false)}
        />
      </Modal>

      <MemberFormModal
        open={showEditMember}
        onClose={() => setShowEditMember(false)}
        title="Editează membru"
        submitLabel="Salvează"
        initial={memberEditInitial}
        onSubmit={async (payload) => {
          await familiesApi.updateMember(member.id, {
            name: payload.name,
            date_of_birth: payload.date_of_birth || undefined,
            gender: payload.gender,
            member_relationship: payload.member_relationship,
            notes: payload.notes,
          });
          await loadMemberAndOptions();
        }}
      />

      {canManageShare && (
        <ShareInviteModal
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          kind="member"
          memberId={member.id}
        />
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Șterge membru"
        variant="danger"
        confirmLabel="Șterge"
        cancelLabel="Anulează"
        busy={deletingMember}
        onConfirm={confirmDeleteMember}
        onCancel={() => {
          if (!deletingMember) setShowDeleteConfirm(false);
        }}
        message={
          <>
            <p style={{ margin: 0 }}>
              Ștergi definitiv membrul{' '}
              <strong>{memberDisplayName(member)}</strong>?
            </p>
            <p style={{ margin: 0, color: 'var(--color-slate-600)' }}>
              Toate documentele, dosarul medical și partajările active vor fi
              șterse permanent. Acțiunea nu poate fi anulată.
            </p>
          </>
        }
      />
    </div>
  );
};

export default MemberDetail;
